import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Page } from '@zillow/constellation-10';
import { parseQueryString } from '@zg-rentals/string-utils';
import { useRentalManagerExternalReportWebApiHandler_spamInquiry } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { logError } from '@zg-rentals/log-error';

import { AppSelectors } from '../../selectors';
import UserActions from '../../actions/UserActions';
import stringHelper from '../../utils/stringHelper';
import MarkAsSpam from './MarkAsSpam';
import ContactActions from './ContactActions';

const InquiryContact = () => {
  const dispatch = useDispatch();

  const email = useSelector((state) => state.user.email);
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));
  const queryString = useSelector((state) => state.router.location.search);

  const {
    intentionType: initialIntentionType,
    inquiryId,
    controlHash,
    landlordBrand,
    renterBrand,
    name,
    phone,
    date,
  } = useMemo(() => parseQueryString(queryString), [queryString]);

  if (!inquiryId || !controlHash) {
    logError({
      error: new Error(`Inquiry-Contact called with no inquiryId or controlHash urlParams:${queryString}`),
      errorType: 'InquiryContactError',
      context: 'InquiryContact.constructor',
    });
  }

  const sanitizedName = stringHelper.sanitizeReactInput(name);
  const sanitizedPhone = stringHelper.sanitizeReactInput(phone);
  const sanitizedDate = stringHelper.sanitizeReactInput(date);

  const [intentionType, setIntentionType] = React.useState(initialIntentionType);

  const { mutate: reportSpamMutation } = useRentalManagerExternalReportWebApiHandler_spamInquiry();

  const saveIntention = useCallback(
    (type) => {
      dispatch(
        UserActions.insertAction({
          intentionType: type,
          inquiryId,
          controlHash,
          landlordBrand,
          renterBrand,
        }),
      );
    },
    [dispatch, inquiryId, controlHash, landlordBrand, renterBrand],
  );

  const reportSpam = useCallback(() => {
    reportSpamMutation({
      getParams: {
        inquiryId: Number(inquiryId),
        controlHash,
        reporterEmail: email,
        comment: '',
      },
    });
  }, [reportSpamMutation, inquiryId, controlHash, email]);

  const setIntention = useCallback(
    (intention) => {
      setIntentionType(intention);

      if (intention === 'reportInquiryAsSpam') {
        saveIntention(intention);
      }
    },
    [saveIntention],
  );

  const executeAction = useCallback(
    (type, sendIntention = true) => {
      if (type === 'call') {
        if (sendIntention) {
          saveIntention(type);
        }
        location.href = `tel:${sanitizedPhone}`;
      } else if (type === 'sms') {
        if (sendIntention) {
          saveIntention(type);
        }
        location.href = `sms:${sanitizedPhone}`;
      }

      setIntention(type);
    },
    [saveIntention, setIntention, sanitizedPhone],
  );

  const renderIntention = (type) => {
    switch (type) {
      case 'call':
      case 'sms':
        return (
          <ContactActions
            onExecuteAction={executeAction}
            intentionType={intentionType}
            name={sanitizedName}
            phone={sanitizedPhone}
            isMobile={!isScreenDesktop}
          />
        );
      case 'reportInquiryAsSpam':
        return <MarkAsSpam reportSpam={reportSpam} date={sanitizedDate} name={sanitizedName} />;
      default:
        return <div />;
    }
  };

  return (
    <Page.Root
      css={{
        marginTop: { base: '600', lg: '1400' },
        backgroundColor: 'bg.neutral',
        lg: { padding: '800' },
        '& .HR': { margin: '{spacing.400} 0' },
      }}
    >
      <Page.Content>{renderIntention(intentionType)}</Page.Content>
    </Page.Root>
  );
};

export default InquiryContact;
